'use client';

import VideoCarousel from '@/shared/components/VideoCarousel';
import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';

export function HeroSection() {
	const router = useRouter();
	const [placeholder, setPlaceholder] = useState('');
	const [displayedText, setDisplayedText] = useState('');
	const [isTyping, setIsTyping] = useState(true);

	// Typewriter effect
	useEffect(() => {
		if (!placeholder) return; // Don't start typing if placeholder is empty

		setDisplayedText('');
		setIsTyping(true);
		let currentIndex = 0;

		const typingInterval = setInterval(() => {
			if (currentIndex < placeholder.length) {
				setDisplayedText(placeholder.substring(0, currentIndex + 1));
				currentIndex++;
			} else {
				setIsTyping(false);
				clearInterval(typingInterval);
			}
		}, 50); // 50ms per character - adjust speed here

		return () => clearInterval(typingInterval);
	}, [placeholder]);

	return (
		<>
			{/* Fullscreen Video Background */}
			<div className="fixed inset-0 z-0">
				<VideoCarousel onPlaceholderChange={setPlaceholder} />
			</div>

			{/* Hero Section */}
			<section className="relative z-10 min-h-screen flex items-center justify-center px-6 pt-20">
				<div className="container mx-auto text-center py-20 max-w-7xl">
					<h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight mb-8 leading-tight text-white drop-shadow-2xl px-6 md:px-8">
						<span className="bg-gradient-to-r from-blue-500 via-white to-red-500 bg-clip-text text-transparent animate-gradient bg-[length:200%_auto] font-light break-words inline-block">
							Your Studio, Reimagined
						</span>
					</h1>

					<p className="mx-auto max-w-3xl text-xl md:text-2xl text-white/90 mb-12 leading-relaxed drop-shadow-lg px-4">
						Transform your ideas into captivating visual stories
						with <b>TabStudio</b>
					</p>

					{/* Optional: Input prompt demo */}
					<div className="relative max-w-3xl mx-auto">
						<div className="glass-frosted glass-hover relative rounded-3xl sm:rounded-full p-3 sm:p-2 shadow-2xl">
							<div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 px-2 sm:px-4 py-2 sm:py-3">
								<div className="flex items-center gap-3 w-full sm:flex-1 min-w-0">
									<svg
										className="w-5 h-5 text-white/60 flex-shrink-0"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
										/>
									</svg>
									<div className="flex-1 min-w-0 text-white/70 text-sm md:text-base leading-relaxed py-1 min-h-[1.5rem] flex items-center">
										<span>
											{displayedText}
											{isTyping && (
												<span className="inline-block w-0.5 h-4 bg-white/70 ml-0.5 animate-pulse align-middle" />
											)}
										</span>
									</div>
								</div>
								<button
									onClick={() => router.push('/login')}
									className="glass-gradient glass-hover w-full sm:w-auto px-6 py-3 sm:py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-full font-semibold text-sm border border-white/20 flex-shrink-0 shadow-lg shadow-red-500/30"
								>
									Produce
								</button>
							</div>
						</div>
					</div>

					{/* Powered by */}
					<div className="flex items-center justify-center gap-2 mt-4">
						<span className="text-xs text-white/60">
							Powered by
						</span>
						<Image
							src="/ilmu-logo.png"
							alt="Ilmu Logo"
							width={60}
							height={20}
							className="h-5 w-auto"
							loading="lazy"
							unoptimized
						/>
					</div>
				</div>
			</section>
		</>
	);
}
